import { useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"

type Props = {
  /** Текущее имя версии (может быть null — тогда в инпуте пусто, а на кнопке label). */
  name: string | null
  /** Отображаемая подпись (имя или дефолт вида v1). */
  label: string
  title: string
  onCommit: (name: string) => void
  className?: string
  maxLength?: number
  /** Пустая строка тоже сохраняется. Без флага пустой ввод отменяется. */
  allowEmpty?: boolean
}

/**
 * Инлайн-редактирование имени версии: кнопка ↔ компактный инпут.
 * Высота в обоих состояниях одна (по умолчанию h-[17px]), чтобы переключение
 * не сдвигало окружающую вёрстку по вертикали.
 */
export function VersionNameInlineEdit({
  name,
  label,
  title,
  onCommit,
  className,
  maxLength = 80,
  allowEmpty = false,
}: Props) {
  const [editing, setEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const closedRef = useRef(false)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const close = (save: boolean) => {
    if (closedRef.current) return
    closedRef.current = true
    setEditing(false)
    if (!save) return
    const next = inputRef.current?.value.trim() ?? ""
    if (!allowEmpty && !next) return
    if (next !== (name ?? "")) onCommit(next)
  }

  const begin = () => {
    closedRef.current = false
    setEditing(true)
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        defaultValue={name ?? ""}
        maxLength={maxLength}
        aria-label={title}
        className={cn(
          "box-border h-[17px] w-full min-w-0 rounded border border-ui-accent bg-transparent px-1 py-0 text-[10px] leading-none font-semibold text-ui-text-primary outline-none",
          className,
        )}
        onBlur={() => close(true)}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === "Enter") {
            e.preventDefault()
            close(true)
          } else if (e.key === "Escape") {
            e.preventDefault()
            close(false)
          }
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      />
    )
  }

  return (
    <button
      type="button"
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        begin()
      }}
      onDoubleClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(
        "box-border h-[17px] min-w-0 truncate rounded border border-transparent px-1 text-left text-[10px] leading-none font-semibold text-ui-text-primary transition-colors hover:bg-ui-surface-raised",
        className,
      )}
    >
      {label}
    </button>
  )
}
